import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDebouncedValue } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { dataLibraryApi, fetchAllPages, MAX_GROUPED_ROWS } from '../../../services/api/dataLibrary';
import type { DataLibrarySurveyParams } from '../../../services/api/dataLibrary';
import type {
  DataLibrarySurveyRow,
  DataLibrarySurveyQuestion,
  DataLibrarySurveyFilterOptions,
  ColumnAverages,
} from '../../../types';
import type { SortConfig, ColumnConfig } from '../../../hooks/useColumnConfig';
import type { ActiveFilterRow } from '../../common/FilterPanel/FilterPanel';
import type { SortState } from '../../../utils/sortTasks';
import type { GroupState } from '../../common/GroupPanel/GroupPanel';
import { groupDataByLevels, type GroupNode } from '../../../utils/groupDataByField';
import { useDataLibraryColumns } from '../useDataLibraryTableLayout';

interface UseDataLibrarySurveyResponsesProps {
  selectedCohortIds: string[];
  formTypes: string[];
  activeGroup: GroupState | null;
  onGroupChange: (group: GroupState | null) => void;
  /** MED-773: Initial filters when opened from drill-down with group selection */
  initialFilters?: ActiveFilterRow[];
}

// Static columns that appear before dynamic question columns
const STATIC_COLUMNS: ColumnConfig[] = [
  { id: 'email', label: 'Email', visible: true, sortable: true, groupable: false },
  { id: 'first_name', label: 'First Name', visible: true, sortable: true, groupable: false },
  { id: 'last_name', label: 'Last Name', visible: true, sortable: true, groupable: false },
  { id: 'full_name', label: 'Full Name', visible: true, sortable: false, groupable: false },
  { id: 'cohort_name', label: 'Cohort', visible: false, sortable: false },
  { id: 'cohort_health', label: 'Cohort Health', visible: false, sortable: false, groupable: false, exportKey: null },
  { id: 'attendance_rate', label: 'Attendance Rate', visible: true, sortable: true, groupable: false, numeric: 'percent' },
  // MED-1266 (AC12): marking coverage column (guarded — survey rows may not carry it yet).
  { id: 'attendance_coverage', label: 'Attendance Coverage', visible: false, sortable: false, groupable: false, exportKey: null },
  // MDF-212: the participant's average engagement, as on the Participants tab.
  { id: 'avg_engagement', label: 'Avg Engagement', visible: true, sortable: true, groupable: false, exportKey: 'avg_engagement', numeric: 'decimal' },
  { id: 'group_name', label: 'Group', visible: true, sortable: true },
  { id: 'coach_name', label: 'Coach', visible: true, sortable: true },
  { id: 'overall_satisfaction', label: 'Overall Satisfaction', visible: true, sortable: false },
  { id: 'has_open_flag', label: 'Flagged', visible: false, sortable: false, groupable: false, exportKey: null },
];

const MID_VS_END_EXTRA_COLUMNS: ColumnConfig[] = [
  { id: 'submission_id', label: 'Record ID', visible: false, sortable: false, groupable: false },
  { id: 'survey_stage', label: 'Survey Stage', visible: true, sortable: false },
];

export function useDataLibrarySurveyResponses({
  selectedCohortIds,
  formTypes,
  activeGroup,
  initialFilters,
}: UseDataLibrarySurveyResponsesProps) {
  const isMidVsEnd = formTypes.length > 1;

  // Data state
  const [rows, setRows] = useState<DataLibrarySurveyRow[]>([]);
  const [questions, setQuestions] = useState<DataLibrarySurveyQuestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  // MDF-41: server averages over all matching rows, by column id
  const [averages, setAverages] = useState<ColumnAverages>({});
  const [totalPages, setTotalPages] = useState(0);
  const [filterOptions, setFilterOptions] = useState<DataLibrarySurveyFilterOptions>({
    groups: [],
    coaches: [],
  });
  const [hasSurveyConfig, setHasSurveyConfig] = useState(true);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  // Search
  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchValue, 300);

  // Sort
  const [sortConfig, setSortConfig] = useState<SortConfig | null>({
    key: 'first_name',
    order: 'ASC',
  });

  // Filters
  const [activeQuickFilters, setActiveQuickFilters] = useState<string[]>([]);
  const [activeFilters, setActiveFilters] = useState<ActiveFilterRow[]>(initialFilters ?? []);

  // Column config — built dynamically from static + question columns
  const defaultColumns: ColumnConfig[] = useMemo(() => {
    // MED-750: email must be the first column even in mid-vs-end view
    const baseStatic = isMidVsEnd
      ? [STATIC_COLUMNS[0], ...MID_VS_END_EXTRA_COLUMNS, ...STATIC_COLUMNS.slice(1)]
      : [...STATIC_COLUMNS];

    const GROUPABLE_QUESTION_TYPES = new Set(['choices', 'yesNo', 'scale']);
    const questionCols: ColumnConfig[] = questions.map(q => ({
      id: `q_${q.question_id}`,
      label: q.question_text,
      visible: true,
      sortable: false,
      groupable: GROUPABLE_QUESTION_TYPES.has(q.question_type),
    }));

    return [...baseStatic, ...questionCols];
  }, [questions, isMidVsEnd]);

  // MDF-209: saved order and visibility for this tab
  const { columns, setColumns } = useDataLibraryColumns(defaultColumns);

  const visibleColumns = useMemo(() => columns.filter(c => c.visible), [columns]);

  // Build API params
  const buildParams = useCallback((): DataLibrarySurveyParams => {
    const params: DataLibrarySurveyParams = {
      cohortIds: selectedCohortIds,
      formTypes,
      page,
      limit: pageSize,
      ...(sortConfig && { sortBy: sortConfig.key, sortOrder: sortConfig.order }),
    };

    if (debouncedSearch) params.search = debouncedSearch;
    if (activeQuickFilters.includes('flagged')) params.flaggedOnly = true;
    if (activeQuickFilters.includes('absences')) params.hasAbsences = true;

    for (const filter of activeFilters) {
      if (filter.values.length === 0) continue;
      switch (filter.key) {
        case 'group':
          params.groupId = filter.values.join(',');
          break;
        case 'coach':
          params.coachId = filter.values[0];
          break;
      }
    }

    return params;
  }, [selectedCohortIds, formTypes, page, pageSize, sortConfig, debouncedSearch, activeQuickFilters, activeFilters]);

  // MDF-39: the filtered total when it is over MAX_GROUPED_ROWS; the table then stays paged
  const [overGroupLimit, setOverGroupLimit] = useState<number | null>(null);
  const isGrouped = !!activeGroup && overGroupLimit === null;
  // MDF-39: only the newest request may write state; a grouped load spans many requests
  const latestRequest = useRef(0);

  // Fetch
  const fetchData = useCallback(async () => {
    const requestId = ++latestRequest.current;
    if (selectedCohortIds.length === 0) {
      setRows([]);
      setQuestions([]);
      setTotalCount(0);
      setAverages({});
      setTotalPages(0);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const params = buildParams();
      // MDF-39: a grouped table needs every row, so it fetches all pages
      const result = isGrouped
        ? await fetchAllPages((page, limit) => dataLibraryApi.getSurveyResponses({ ...params, page, limit }), () => requestId !== latestRequest.current)
        : await dataLibraryApi.getSurveyResponses(params);
      if (requestId !== latestRequest.current) return;
      const total = result.meta.pagination.total;
      setOverGroupLimit(total > MAX_GROUPED_ROWS ? total : null);
      if (isGrouped && total > MAX_GROUPED_ROWS) return;

      setRows(result.data || []);
      setQuestions(result.meta.questions || []);
      setTotalCount(result.meta.pagination.total);
      setAverages(result.meta.averages);
      setTotalPages(result.meta.pagination.totalPages);
      setFilterOptions(result.meta.filterOptions);
      setHasSurveyConfig(result.meta.hasSurveyConfig);
    } catch {
      if (requestId !== latestRequest.current) return;
      notifications.show({
        title: 'Error',
        message: 'Failed to load survey responses',
        color: 'red',
      });
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  }, [selectedCohortIds, buildParams, isGrouped]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset page on filter/search change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, activeQuickFilters, activeFilters, selectedCohortIds]);

  // Sort handlers
  const handleSort = useCallback((key: string) => {
    setSortConfig(prev => ({
      key,
      order: prev?.key === key && prev.order === 'ASC' ? 'DESC' : 'ASC',
    }));
  }, []);

  const handleSortPanelChange = useCallback((sort: SortState | null) => {
    if (sort) {
      setSortConfig({ key: sort.field, order: sort.direction === 'asc' ? 'ASC' : 'DESC' });
    } else {
      setSortConfig(null);
    }
  }, []);

  // Filter handlers
  const handleQuickFilterToggle = useCallback((key: string) => {
    setActiveQuickFilters(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key],
    );
  }, []);

  const handleFilterAdd = useCallback((key: string) => {
    setActiveFilters(prev => {
      if (prev.some(f => f.key === key)) return prev;
      return [...prev, { key, values: [] }];
    });
  }, []);

  const handleFilterChange = useCallback((key: string, values: string[]) => {
    setActiveFilters(prev => prev.map(f => (f.key === key ? { ...f, values } : f)));
  }, []);

  const handleFilterRemove = useCallback((key: string) => {
    setActiveFilters(prev => prev.filter(f => f.key !== key));
  }, []);

  const clearAllFilters = useCallback(() => {
    setActiveQuickFilters([]);
    setActiveFilters([]);
    setSearchValue('');
  }, []);

  const hasActiveFilters =
    activeQuickFilters.length > 0 ||
    activeFilters.some(f => f.values.length > 0) ||
    debouncedSearch.length > 0;

  // Client-side filters (overall_satisfaction, survey_stage)
  const filteredRows = useMemo(() => {
    let result = rows;

    for (const filter of activeFilters) {
      if (filter.values.length === 0) continue;
      if (filter.key === 'overall_satisfaction') {
        result = result.filter(r => r.overall_satisfaction && filter.values.includes(r.overall_satisfaction));
      }
      if (filter.key === 'survey_stage') {
        result = result.filter(r => filter.values.includes(r.survey_stage));
      }
    }

    return result;
  }, [rows, activeFilters]);

  // Client-side grouping
  const groupedRows = useMemo(() => {
    if (!activeGroup || !isGrouped) return null;
    let source = filteredRows;
    let levels = activeGroup;
    if (isMidVsEnd && activeGroup.some(l => l.field === 'participant')) {
      // "Participant" groups by email, A to Z, with Midpoint before Endpoint in each
      // group. The sort is stable, so sorting first keeps that order after grouping.
      const stageOrder: Record<string, number> = { Midpoint: 0, Endpoint: 1 };
      source = [...filteredRows].sort(
        (a, b) => (stageOrder[a.survey_stage] ?? 2) - (stageOrder[b.survey_stage] ?? 2),
      );
      levels = activeGroup.map(l =>
        l.field === 'participant' ? { field: 'email', order: 'alphabetical' as const } : l,
      );
    }
    return groupDataByLevels(
      source as unknown as Record<string, unknown>[],
      levels,
      columns,
    ) as GroupNode<DataLibrarySurveyRow>[] | null;
  }, [filteredRows, isMidVsEnd, activeGroup, isGrouped, columns]);

  return {
    groupLimitTotal: activeGroup ? overGroupLimit : null,
    rows: filteredRows,
    groupedRows,
    hasSurveyConfig,
    questions,
    loading,
    totalCount,
    averages,
    totalPages,
    filterOptions,
    page,
    setPage,
    pageSize,
    setPageSize: (size: number) => {
      setPageSize(size);
      setPage(1);
    },
    searchValue,
    setSearchValue,
    sortConfig,
    handleSort,
    handleSortPanelChange,
    activeQuickFilters,
    handleQuickFilterToggle,
    activeFilters,
    handleFilterAdd,
    handleFilterChange,
    handleFilterRemove,
    clearAllFilters,
    hasActiveFilters,
    columns,
    setColumns,
    visibleColumns,
    isMidVsEnd,
  };
}
